import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Button, Table } from "@pentabd/ui";

import { AssetsListTableColumn } from "./AssetsListTableColumn";
import { useLanguage } from "@hooks/useLanguage";
import { Pagination } from "@type/inventory/pagination";
import { SearchBox, SelectControllerVertical } from "@components";
import { useEmployeeAssets } from "@hooks/inventory/employee/useEmployeeAssets";
import { AssetsResponseType, EmployeePropType } from "@type/inventory/employee";
import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";
import { useMetaData } from "@hooks/inventory/settings/useMetaData";
import { SETTINGS_META_DATA } from "@constants/inventory/settings-meta-data";

import { ROUTES } from "@constants/routes";
import { TablePagination } from "@helpers/tablePagination";

const { INVENTORY, CREATE } = ROUTES;
const { MAINTENANCE } = INVENTORY;

const { CATEGORY, GROUP, CONDITION } = SETTINGS_META_DATA;

export default function AssetsListTable({
  id,
  isProfile,
  isProfileDisabled,
}: EmployeePropType) {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { control, register, setValue } = useForm();

  const { data: categories } = useMetaData(CATEGORY);
  const { data: groups } = useMetaData(GROUP);
  const { data: conditions } = useMetaData(CONDITION);

  const { handleQuery, employeesState } = useEmployeeAssets(id);

  const { assets } = employeesState;

  const { data, pagination } = assets || ({} as AssetsResponseType);

  const { total } = pagination || ({} as Pagination);

  return (
    <div className="pt-12 border-top">
      <Table
        header={{
          title: t("INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.TITLE"),
          buttons: [
            isProfile && !isProfileDisabled ? (
              <Button
                type="info"
                fill="fill"
                onClick={() => navigate(`${MAINTENANCE}/${CREATE}`)}
              >
                {t(
                  "INVENTORY_MAINTENANCE.TABLE.REQUEST_MAINTENANCE_BUTTON_TEXT",
                )}
              </Button>
            ) : (
              <></>
            ),
          ],
          badge: {
            label: `${total || 0}`,
            size: "sm",
            type: "primary",
          },
        }}
        headerExtension={{
          leftComponents: [
            <div className="row pb-0 mb-0">
              <SelectControllerVertical
                label="INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.FILTER.CATEGORY"
                name="category"
                register={{ ...register("category") }}
                options={getConvertedMetaData(categories)}
                setValue={setValue}
                selectAction={(e) => handleQuery("category", e)}
                control={control}
                cols={3}
                portal
                withoutLabel
              />
              <SelectControllerVertical
                label="INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.FILTER.GROUP"
                name="group"
                register={{ ...register("group") }}
                options={getConvertedMetaData(groups)}
                setValue={setValue}
                selectAction={(e) => handleQuery("group", e)}
                control={control}
                cols={2}
                portal
                withoutLabel
              />
              <SelectControllerVertical
                label="INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.FILTER.CONDITION"
                name="condition"
                register={{ ...register("condition") }}
                options={getConvertedMetaData(conditions)}
                setValue={setValue}
                selectAction={(e) => handleQuery("condition", e)}
                control={control}
                cols={2}
                portal
                withoutLabel
              />
              <SelectControllerVertical
                label="INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.FILTER.TYPE"
                name="type"
                register={{ ...register("type") }}
                options={[
                  {
                    label: language === "bn" ? "সম্পদ" : "Asset",
                    value: "ASSET",
                  },
                  {
                    label: language === "bn" ? "আইটেম" : "Item",
                    value: "ITEM",
                  },
                ]}
                setValue={setValue}
                selectAction={(e) => handleQuery("type", e)}
                control={control}
                cols={2}
                portal
                withoutLabel
              />
              <SelectControllerVertical
                label="INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.FILTER.SORTING"
                name="sorting"
                register={{ ...register("sorting") }}
                options={[
                  {
                    label: language === "bn" ? "আরোহী" : "Ascending",
                    value: "ASC",
                  },
                  {
                    label: language === "bn" ? "অবরোহী" : "Descending",
                    value: "DESC",
                  },
                ]}
                setValue={setValue}
                selectAction={(e) => handleQuery("sorting", e)}
                control={control}
                cols={3}
                portal
                withoutLabel
              />
            </div>,
          ],
          rightComponents: [
            <SearchBox
              key="search"
              control={control}
              placeholder={t(
                "INVENTORY_EMPLOYEE.EMPLOYEE_DETAILS.ASSETS.TABLE.SEARCH_BUTTON",
              )}
              handleQuery={handleQuery}
            />,
          ],
        }}
        columns={AssetsListTableColumn(t, language)}
        rows={data || []}
        pagination={TablePagination(pagination, handleQuery)}
      />
    </div>
  );
}
